import ExpiresAfter from '../../domain/ExpiresAfter';
import {FileMeta, transferApi, type UploadInitRequest} from '../../api/transferApi';
import type {PersonalizationDraftRequest} from '../../home/personalization/personalizationDraft';
import type {SenderCompany} from '../../sender/senderChoice';
import {sanitizeRelativePath} from '../transfer/relativePath';
import {SelectedFileInput, toSelectedFile} from '../transfer/selection';
import sha3 from 'crypto-js/sha3';

/**
 * The contract every hosted upload shares with its callers: what the composer hands in
 * ({@link UploadSettings}), what comes back ({@link UploadResult}), how progress and a cancel are
 * reported, and how a selected file is declared to the api ({@link toFileMeta}).
 *
 * A module of its own rather than part of `EncryptedUploadClient` because the modules that consume it
 * sit on both sides of that client: `transientRetry` and `uploadProgress` are its building blocks, while
 * `TransferService`, the composer and the add-files flow are its callers. It held the plaintext
 * `UploadClient` too until kitecorp/transferify-ui#3053 removed that engine; every transfer encrypts in
 * the browser (ui#2708), and `uploadBoundary.test.ts` keeps it that way.
 */

/**
 * How the AES file key is handled for an encrypted transfer (epic #61 Option A):
 *  - {@code "escrow"} (default): wrap the key with the server's KMS public key and send the wrapped
 *    blob to the server (encryption-at-rest, short fragment-less recoverable link) — the server CAN
 *    decrypt via KMS. All tiers.
 *  - {@code "zeroKnowledge"} (PRO): the raw key rides only in the URL fragment (`#k=`); nothing
 *    key-related is ever sent, so the server literally cannot decrypt. Gated by `canUseZeroKnowledge`.
 */
export type KeyMode = "escrow" | "zeroKnowledge";

export interface UploadSettings {
	message?: string;
	expireAfter: ExpiresAfter;
	peerId?: string;
	/**
	 * Optional download password: standard/escrow init forwards it for API enforcement;
	 * Zero-Knowledge uses it only to wrap the URL-fragment key so the server remains blind.
	 */
	password?: string;
	/** Optional download cap (API contract v3); forwarded verbatim on init. */
	maxDownloads?: number;
	/** Per-transfer download-alerts opt-in (api#92); forwarded verbatim for API capability enforcement. */
	notifyOnDownload?: boolean;
	/** Per-transfer expiry-recovery opt-in (api#443, Premium); absent = bytes die with the link. */
	recoveryOptIn?: boolean;
	/** Key-handling flow for encrypted transfers; defaults to {@code "escrow"} when absent. */
	keyMode?: KeyMode;
	/** Direct recipients for authenticated preflight; only supplied by recipient transfers (ui#231). */
	toEmails?: string[];
	/** Saved group ids for authenticated preflight; only supplied by recipient transfers (ui#231). */
	groupIds?: string[];
	/**
	 * Per-transfer personalization (api#461); absent = unpersonalized. Forwarded verbatim on init, so a
	 * personalized transfer carries the sender's whole composition whichever path they took.
	 */
	personalization?: PersonalizationDraftRequest;
	/**
	 * The company the transfer is sent as (kitecorp/transferify-ui#3186), forwarded verbatim on init; absent sends it
	 * as a person.
	 */
	senderCompany?: SenderCompany;
	/**
	 * The personalization to attach when the bytes have landed, asked for at that moment (ui#1814,
	 * api#1099).
	 *
	 * A FUNCTION rather than a value, and that is the whole point: the preview-first builder opens in
	 * the upload's dead time, so what the sender composes does not exist when {@link personalization}
	 * above is read. Reading it here — immediately before `POST .../complete` — is what lets a draft
	 * written while the progress bar moved reach the transfer it belongs to.
	 *
	 * Absent, or returning `null`, leaves the transfer exactly as the init left it. The api treats a
	 * completion with no `personalization` key as untouched, so nothing about the existing path changes.
	 *
	 * AWAITED (ui#1839), so an unanswered provider holds the completion open — one that can wait on a
	 * person must therefore carry its own bound, because nothing here imposes one.
	 *
	 * No longer the only route a late look takes. A personalization composed once the success screen
	 * is up goes to `POST /transfer/{id}/personalization` (api#1592) instead, which takes a
	 * peer-to-peer transfer as readily as a hosted one.
	 */
	personalizationAtCompletion?: () => PersonalizationDraftRequest | null
		| Promise<PersonalizationDraftRequest | null>;
	/**
	 * The settled over-limit purchase this transfer spends (api#823, ui#1454).
	 *
	 * NAMED, never auto-applied: the api spends only the allowance the init points at, so a 1 TB
	 * purchase can never be quietly consumed by a 6 GB send. An id that does not cover the declared
	 * size is refused with `error.transfer.overLimit.notConsumable`, and an over-cap init carrying none
	 * is refused with `error.transfer.overLimit.paymentRequired`. Absent = an ordinary within-cap send.
	 */
	overLimitAllowanceId?: string;
}

export interface UploadResult {
	transferId: string;
	url: string;
}

/** Who a hosted create is reserved for (ui#231), and the id its retry claims the same row with (api#136). */
export interface HostedCreateRecipients {
	toEmails?: string[];
	groupIds?: string[];
	initializationId?: string;
}

/**
 * Everything a hosted create declares besides its files: the upload init (`EncryptedUploadClient`) and the new
 * link to stored files (`core/transfer/storedLink`, kitecorp/transferify-ui#3405) send the same options, so they
 * are written once here.
 *
 * `escrow` decides the key fields: an escrow create forwards the download password for the api to enforce, while
 * a Zero-Knowledge one keeps it off the wire, where it wraps the key in the link's fragment instead. Every hosted
 * create is encrypted (ui#2708). Each optional field is left out when it is absent, as the api reads absent as
 * "none"; an empty recipient list is absent.
 */
export function hostedCreateFields(settings: UploadSettings, escrow: boolean,
								   {toEmails, groupIds, initializationId}: HostedCreateRecipients): Omit<UploadInitRequest, 'files'> {
	return {
		message: settings.message,
		keepOnline: true,
		expireAfter: settings.expireAfter,
		peerId: settings.peerId,
		...(escrow && settings.password ? {password: settings.password} : {}),
		maxDownloads: settings.maxDownloads,
		notifyOnDownload: settings.notifyOnDownload,
		recoveryOptIn: settings.recoveryOptIn,
		encrypted: true,
		keyEscrow: escrow,
		// api#461: the personalization rides the CREATE, because the api takes its snapshot there. Encryption
		// changes nothing about it — the visuals are the sender's own and are served to recipients from the
		// api, never from the encrypted payload.
		...(settings.personalization ? {personalization: settings.personalization} : {}),
		// kitecorp/transferify-ui#3186: the company the transfer is sent as, a creation fact like the look.
		...(settings.senderCompany ? {senderCompany: settings.senderCompany} : {}),
		// ui#1454 / api#823: the paid over-cap allowance, NAMED on the create. Nothing is applied server-side
		// without it, so a create that omits it is refused with TRANSFER_OVER_LIMIT_PAYMENT_REQUIRED however
		// thoroughly the sender paid — which is the 400 the owner met on 2026-08-30 (ui#1716).
		...(settings.overLimitAllowanceId ? {overLimitAllowanceId: settings.overLimitAllowanceId} : {}),
		// ui#2708: recipient sends encrypt like every other hosted create, so the recipient reservation (ui#231)
		// and its idempotent retry key ride the create.
		...(toEmails?.length ? {toEmails} : {}),
		...(groupIds?.length ? {groupIds} : {}),
		...(initializationId ? {initializationId} : {}),
	};
}

/**
 * Reports cumulative upload progress in bytes (0..total) as part bytes are sent and parts finish. Never
 * reports a lower value than before, even when a failed part is re-sent (ui#2933).
 */
export type ProgressListener = (bytesSent: number, totalBytes: number) => void;

/** Reports per-file upload progress the same way; `fileIndex` matches the uploaded files array. */
export type FileProgressListener = (fileIndex: number, fileBytesSent: number, fileTotalBytes: number) => void;

/** Thrown by an upload when the user cancels it through the client's `abort`. */
export class UploadAbortedError extends Error {
	constructor() {
		super('Upload cancelled');
		this.name = 'UploadAbortedError';
	}
}

/** Throws {@link UploadAbortedError} when the user cancelled. */
export function throwIfAborted(signal: AbortSignal | undefined): void {
	if (signal?.aborted) {
		throw new UploadAbortedError();
	}
}

/** SHA-3 request identity: callers retain this digest only, never the sensitive source values. */
export function secureRequestDigest(request: unknown): string {
	return sha3(JSON.stringify(request)).toString();
}

/**
 * Tells the api a transfer was ABANDONED, so it can take the row down and hand back anything the creation
 * already spent on it (api kitecorp/transferify-api-service#1080, ui kitecorp/transferify-ui#1788). A cancel
 * abandons one, and so does a failed LINK create, whose row would otherwise hold the sender's storage as a
 * reservation until it expired (kitecorp/transferify-api-service#2227).
 *
 * The init creates the transfer and SPENDS the sender's over-limit purchase on it before any byte moves, so
 * a cancel that only cleaned up storage parts left a paid-for send abandoned and its purchase spent for
 * good. The per-file multipart abort cannot stand in for this — it cleans up storage, keeps the row, and
 * there is frequently no multipart upload minted yet to abort at all.
 *
 * BEST EFFORT, and it must stay that way: the local cancel has already happened and the sender is on their
 * way back to the composer, so a failed void may not turn a cancel into an error. What is lost is the
 * refund, which the api's own expiry and the sender's next attempt can still recover.
 *
 * BOUNDED for the same reason. The caller AWAITS this before rejecting, and the composer only leaves the
 * progress screen on that rejection — so an unbounded request would hold a sender on a progress card whose
 * Cancel has already done its job, with a reload as their only way out. Axios applies no timeout of its own
 * and this app sets no global default, which is precisely the shape that turned a hung request into a dead
 * surface once before (the over-limit quote, ui#1715). The bound is short because the whole point is that
 * the sender is already leaving: on the network conditions that make somebody cancel a multi-gigabyte
 * upload, waiting longer buys a refund at the cost of the screen they are trying to get back to.
 *
 * @return whether the api voided the transfer — which also aborted every upload it still had in flight —
 * so a caller holding those uploads knows whether releasing them is still its own job
 */
export async function voidCancelledTransfer(transferId: string): Promise<boolean> {
	try {
		await transferApi.cancelUpload(transferId);
		return true;
	} catch {
		// no-state: see above. Nothing on screen depends on this, and the cancel itself already succeeded.
		return false;
	}
}

/**
 * The content type every file declaration carries: the browser's own `File.type`, or
 * `application/octet-stream` when the browser reports `""` because it cannot classify the file. The ONE
 * reading of a file's type for every declaration the api records — hosted init, the E2EE multipart echo,
 * adding files after sending, and peer-to-peer registration (api#1631) — so one file never lands in two
 * buckets of the api's file-type chart depending on how it was sent.
 */
export function declaredContentType(file: Pick<File, 'type'>): string {
	return file.type || 'application/octet-stream';
}

/**
 * Declares one file's metadata (no bytes). Shared by the hosted upload and the instant (P2P)
 * registration, which adds only its peer identity. Path-aware since ui#94: a selection entry's
 * `relativePath` is sanitized (`core/transfer/relativePath`, the same rules the server applies) and
 * OMITTED entirely when absent or rejected — the wire never carries a raw client path.
 */
export function toFileMeta(input: SelectedFileInput): FileMeta {
	const {file, relativePath} = toSelectedFile(input);
	const path = sanitizeRelativePath(relativePath);
	return {
		name: file.name,
		size: file.size,
		contentType: declaredContentType(file),
		...(path !== undefined ? {relativePath: path} : {}),
	};
}

/**
 * Local logical-request identity for one selected file. Unlike {@link toFileMeta}, this never goes on
 * the API wire: {@code lastModified} distinguishes a browser replacement without reading file bytes.
 * Structural test/folder files may omit it, so every absent/non-finite value has one stable {@code null}
 * representation instead of disappearing during JSON serialization.
 */
export function toFileRequestIdentity(input: SelectedFileInput): FileMeta & {lastModified: number | null} {
	const {file} = toSelectedFile(input);
	return {
		...toFileMeta(input),
		lastModified: Number.isFinite(file.lastModified) ? file.lastModified : null,
	};
}
